import { Clear } from "@mui/icons-material";
import {
  Backdrop,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { Fragment, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  getGroupDetails,
  getGroupDetailsWithActions,
  getTaggedAccounts,
} from "../../../services/services";
import { parseDate, parseDateTime } from "../../../utils/tableFunctions";
import TableView from "../../../components/Table";
import { startCase } from "lodash";
import { styles } from "../../../utils/styles";
import moment from "moment";
import PendingDetailsComparison from "../../../components/PendingDetailsComparison";

const getAdminRoleFields = (currentPermissions, originalPermissions) => {
  const permissionKeys = Object.keys({
    ...(currentPermissions || {}),
    ...(originalPermissions || {}),
  }).sort();

  return [
    { key: "name", label: "Name" },
    ...permissionKeys.map((permissionKey) => ({
      key: `permission_${permissionKey}`,
      label: startCase(permissionKey),
      getValue: (role) => role?.permissions?.[permissionKey] || [],
      render: (permissions) => permissions.join(", ") || "N/A",
    })),
  ];
};

const GroupDetails = ({ detailsData, itemId, isViewHistory }) => {
  const [grpDetails, setGrpDetails] = useState();
  const [loaded, setLoaded] = useState();
  const [permission, setPermission] = useState();
  const [accounts, setAccounts] = useState();
  const [taggedAccounts, setTaggedAccounts] = useState();
  const [tableData, setTableData] = useState();
  const { id = itemId } = useParams();
  const useQuery = () => {
    return new URLSearchParams(useLocation().search);
  };
  const action = useQuery().get("action") || (detailsData && "true") || (isViewHistory && "true");
  // Every maker-and-checker view: the pending dialog and the history dialog.
  const isCheckerView = Boolean(action);
  const isHistoryView = Boolean(isViewHistory) || (!detailsData && Boolean(action));
  const mobile = useMediaQuery((theme) => theme.breakpoints.down("lg"));

  useEffect(() => {
    if (!detailsData && !action) {
      getTaggedAccounts(id, setTaggedAccounts);
      getGroupDetails(id, setGrpDetails, setLoaded);
      return;
    }
    if (!detailsData && action) {
      getGroupDetailsWithActions(id, setGrpDetails, setLoaded);
      return;
    }

    setGrpDetails(detailsData);
    setLoaded(true);
  }, [action, detailsData, id]);

  useEffect(() => {
    if (loaded) {
      if (!action) {
        setPermission(grpDetails[1].permission);
        setAccounts(grpDetails[0].accounts);
      } else {
        setPermission(grpDetails[1].permission);
      }
    }
  }, [action, grpDetails, loaded]);

  const formatData = (input) => {
    let tableData = [];
    input.results.forEach(({ id, is_active, email, date_joined }) => {
      tableData.push({
        id: id,
        email: email,
        date_joined: moment(date_joined).format("DD MMM YYYY"),
      });
    });
    return tableData;
  };

  // const getNext = async () => {
  //   if (!next) return;
  //   const res = await getNextPage({
  //     url: next,
  //     filter: filter,
  //   });
  //   let newRows = formatData(res);
  //   setTableData((old) => [...old, ...newRows]);
  //   setNext(res.next ? res.next : false);
  // };

  useEffect(() => {
    if (taggedAccounts) {
      setTableData(formatData(taggedAccounts));
      // setNext(taggedAccounts.next);
    }
  }, [taggedAccounts]);

  const handleBack = () => window.close();
  const pendingRoleData = isCheckerView && grpDetails
    ? { ...grpDetails[0], permissions: grpDetails[1]?.permission }
    : null;
  const originalRoleData = isCheckerView && grpDetails?.[2]?.original
    ? {
        ...grpDetails[2].original.data,
        permissions: grpDetails[2].original.permissions,
    }
    : null;
  // Submitted before change logs existed, so there is no truthful "before" to show.
  const originalUnavailable = Boolean(grpDetails?.[2]?.original_unavailable);
  const formatTimestamp = isHistoryView ? parseDateTime : parseDate;
  const adminRoleFields = getAdminRoleFields(
    pendingRoleData?.permissions,
    originalRoleData?.permissions
  );
  return (
    <Container>
      {!detailsData && !itemId && (
       <Stack
        direction="row"
        sx={{
          ...styles.flexBetween,
          mb: 2,
        }}
      >
        <Typography variant={mobile ? "h5" : "h3"}>Group Details</Typography>

        <Button
          variant="outlined"
          onClick={handleBack}
          endIcon={<Clear />}
        >
          Back
        </Button>
      </Stack> 
      )}

      {loaded && action === "true" && (
        <Typography variant="h6" sx={{ mb: 2 }}>
          Action: &nbsp; {grpDetails[0].action}
        </Typography>
      )}

      {loaded && permission ? (
        <>
          <Card sx={{ p: 3 }}>
            <Grid
              container
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 2, sm: 2, md: 2 }}
              sx={{ mt: 5 }}
            >
              {isCheckerView && !originalUnavailable ? (
                <>
                  <PendingDetailsComparison
                    data={pendingRoleData}
                    original={originalRoleData}
                    fields={adminRoleFields}
                  />
                  <Grid item xs={12} md={6}>
                    <Box sx={{ height: "100%", p: 3, border: "1px solid #999", borderRadius: 2 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={6} md={5}>
                          <Typography variant="subtitle1">
                            {isHistoryView ? "Requested on" : "Created on"}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={7}>
                          {grpDetails?.[0]?.created_at
                            ? formatTimestamp(grpDetails[0].created_at)
                            : "N/A"}
                        </Grid>
                        <Grid item xs={6} md={5}>
                          <Typography variant="subtitle1">
                            {isHistoryView ? "Requested by" : "Created by"}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={7}>
                          {grpDetails?.[0]?.created_by
                            ? grpDetails[0].created_by.email
                            : "N/A"}
                        </Grid>
                        <Grid item xs={6} md={5}>
                          <Typography variant="subtitle1">
                            {isHistoryView ? "Reviewed on" : "Approved on"}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={7}>
                          {grpDetails?.[0]?.approved_at
                            ? formatTimestamp(grpDetails[0].approved_at)
                            : "N/A"}
                        </Grid>
                        <Grid item xs={6} md={5}>
                          <Typography variant="subtitle1">
                            {isHistoryView ? "Reviewed by" : "Approved by"}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={7}>
                          {grpDetails?.[0]?.approved_by
                            ? grpDetails[0].approved_by.email
                            : "N/A"}
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                </>
              ) : (
              <>
              <Grid item xs={6} md={6}>
                <Box
                  sx={{
                    height: "100%",
                    p: 3,
                    border: "1px solid #999",
                    borderRadius: 2,
                  }}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={6} md={5}>
                      <Typography variant="subtitle1">Name</Typography>
                    </Grid>
                    <Grid
                      item
                      xs={6}
                      md={7}
                      sx={{
                        overflowWrap: "normal",
                        wordBreak: "break-word",
                      }}
                    >
                      {grpDetails[0].name}
                    </Grid>

                    <Grid item xs={6} md={5}>
                      <Typography variant="subtitle1">Created by</Typography>
                    </Grid>
                    <Grid item xs={6} md={7}>
                      {grpDetails[0].created_by
                        ? grpDetails[0].created_by.email
                        : "N/A"}
                    </Grid>
                    <Grid item xs={6} md={5}>
                      <Typography variant="subtitle1">Updated by</Typography>
                    </Grid>
                    <Grid item xs={6} md={7}>
                      {grpDetails[0].updated_by
                        ? grpDetails[0].updated_by.email
                        : "N/A"}
                    </Grid>
                    <Grid item xs={6} md={5}>
                      <Typography variant="subtitle1">Approved by</Typography>
                    </Grid>
                    <Grid item xs={6} md={7}>
                      {grpDetails[0].approved_by
                        ? grpDetails[0].approved_by.email
                        : "N/A"}
                    </Grid>
                    <Grid item xs={6} md={5}>
                      <Typography variant="subtitle1">Approved on</Typography>
                    </Grid>
                    <Grid item xs={6} md={7}>
                      {grpDetails[0].approved_at
                        ? parseDate(grpDetails[0].approved_at)
                        : "N/A"}
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid item xs={6} md={6}>
                <Box
                  sx={{
                    height: "100%",
                    px: 3,
                    py: 2,
                    border: "1px solid #999",
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="h6">Permissions</Typography>
                  <Grid
                    container
                    spacing={2}
                    sx={{ mt: 2 }}
                    alignItems="center"
                  >
                    {Object.entries(permission).map(([key, value]) => (
                      <Fragment key={key}>
                        <Grid item xs={6} md={5}>
                          <Typography variant="subtitle1">
                            {startCase(key)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={7}>
                          {value.join(", ") || "N/A"}
                        </Grid>
                      </Fragment>
                    ))}
                  </Grid>
                </Box>
              </Grid>
              </>
              )}
            </Grid>
            {grpDetails[0].reject_message && (
              <Typography variant="subtitle1" sx={{ mt: 2, display: "flex" }}>
                Reason for rejection:
                <Typography sx={{ ml: 1 }}>
                  {" "}
                  {grpDetails[0].reject_message}
                </Typography>
              </Typography>
            )}
            {grpDetails[0].action_message && (
              <Typography variant="subtitle1" sx={{ mt: 2, display: "flex" }}>
                Action message:
                <Typography sx={{ ml: 1 }}>
                  {" "}
                  {grpDetails[0].action_message}
                </Typography>
              </Typography>
            )}
          </Card>
          {!action && accounts && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h5">Accounts</Typography>
              <Box
                sx={{
                  width: "100%",
                  height: 400,
                  maxWidth: 360,
                  bgcolor: "background.paper",
                }}
              >
                <List>
                  {accounts.map((acc) => (
                    <ListItem disablePadding>
                      <ListItemText primary={acc.email} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </>
          )}
        </>
      ) : (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      {tableData && (
        <>
          <Divider sx={{ my: 5 }} />
          <Typography variant="h5" sx={{ mb: 3, textAlign: "center" }}>
            Tagged Accounts
          </Typography>
          <Card>
            <TableView
              rows={tableData}
              count={taggedAccounts.count}
              link={"/3/app/account-details~id~"}
            />
          </Card>
        </>
      )}
    </Container>
  );
};

export default GroupDetails;
